<script lang="ts">
	import { onMount } from 'svelte';
	import { goto } from '$app/navigation';
	import {
		getBrandStatusDisplay,
		BrandStatusEnum,
		BrandStatuses,
		Button,
		Icon,
		Input,
		Label,
		ListingFilterEnum,
		ListingStatus,
		OutboundLink,
		SvelteSelect,
		Table,
		TextArea
	} from '@cf/ui-lib';
	import { ArrowDownIcon, BackArrow, DeleteIcon, SaveIcon } from '@cf/ui-lib/icons';
	import { useKey_valueReadQuery } from '$lib/api-v2/key_valueQuery';
	import { useBrandReadListQuery } from '$lib/api-v2/brandQuery';
	import { StarIcon } from '@cf/ui-lib/icons';
	import { writable } from 'svelte/store';
	import { superForm } from 'sveltekit-superforms/client';
	import { createSellerBrandColumns } from '$lib/columns/seller/create-seller-brand';
	import { getInitialization, getSchema } from '../seller-new/schema';
	import { page } from '$app/stores';
	import {
		useSellersCreateMutation,
		useSellersDeleteMutation,
		useSellerStatusListingCountsMutation,
		useSellersUpdateMutation
	} from '$lib/api-v2/sellersQuery';
	import { useMarketplacesUrlMutation } from '$lib/api-v2/marketplacesQuery';
	import type { TaintedFields } from 'sveltekit-superforms';
	import type { z } from 'zod';
	import type { PageData } from '../seller-new/$types';
	import type { PageData as PageParentDataEdit } from '../seller-details/[slug]/[id]/$types';
	import type {
		Brand,
		PageDataRes,
		ProviderFromDetail,
		PageParentData,
		MarketPlaceAcceptableUrls,
		MarketPlaceAcc
	} from '$lib/types/Seller';
	import MarketPlaceCard from '$lib/components/brand/MarketPlaceCard.svelte';
	import SellerStatus from '$lib/components/brand/SellerStatus.svelte';
	import HeadContent from '$lib/components/headContent.svelte';
	import DetailsCard from '../../../(private)/seller/component/details-card.svelte';
	import {
		addListingsFilter,
		removeAllListingsFilter
	} from '../../../context/slices/listings.slice';
	import Modal from '$lib/components/modal.svelte';
	import ListingCountBySellerStatus from '$lib/components/listings/ListingCountBySellerStatus.svelte';
	import type { SellerStatusListingCounts } from '@cf/api-v2-types';

	type CombinedPageData = ProviderFromDetail & PageDataRes & PageData;
	type CombinedPageParentData = ProviderFromDetail & PageParentData & PageParentDataEdit;

	// export
	export let isEdit: boolean = false;
	export let isClone: boolean = false;
	export let data: CombinedPageData | CombinedPageParentData;

	export let editSellerDataStore = writable({});
	export let cloneSellerDataStore = writable({});

	const IS_POST_MVP = true;

	// Query
	const countryData = useKey_valueReadQuery();
	const brands = useBrandReadListQuery();
	const updateSellerDetail = useSellersUpdateMutation();
	const deleteSeller = useSellersDeleteMutation();
	const creteSellersData = useSellersCreateMutation();
	const getMarketPlaceUrl = useMarketplacesUrlMutation();
	const getSellerStatusListingCounts = useSellerStatusListingCountsMutation();
	const rowDat = writable<
		{
			id: string;
			imgUrl: string;
			name: string;
			licensor: string;
			marketplaces: string[];
			status?: string;
		}[]
	>([]);
	// Get the appropriate schema based on isEdit
	const tab = $page.url.searchParams.get('tab') || '0';
	const schema = getSchema(isEdit, +tab);
	const tabItems = [
		{ name: 'Marketplace Seller' },
		{ name: 'Marketplace Storefront' },
		{ name: 'Social Media Profile' },
		{ name: 'Websites' },
		{ name: 'Creators' }
	];

	const formHeader: string = `New ${tabItems[Number(tab)].name}`;
	const formSubHeader: string = `${tabItems[Number(tab)].name} Details`;
	const initialization = getInitialization(isEdit);
	const { form, errors, enhance, validate, reset, tainted } = superForm(data.form, {
		validators: schema,
		resetForm: false,
		dataType: 'json',
		SPA: true
	});
	const params = new URLSearchParams({
		tab: tab ? tab : '0'
	});
	const backUrl = `/uilib/sellers/list?${params.toString()}`;

	let isTainted: TaintedFields<z.AnyZodObject> | undefined;
	let brandData: {
		id: string;
		imgUrl: string;
		name: string;
		licensor: string;
		marketplaces: string[];
		status?: string;
	}[];
	let formElement: HTMLFormElement | null = null;
	let oldBrandIds: string[];
	let pageSize = 10;
	let pageNumber = 1;
	let selectedRowIds = writable<string[]>([]);
	let selectedMarketplaceBrands = writable<string[]>([]);
	let statusData = writable<{ brand_id: string; status: string; prev_status: string }[]>([]);
	let statusLocal = writable<{ brand_id: string; status: string; prev_status: string }[]>([]);
	let statusDetail: { status: string; brand_id: string }[] = [];
	let filteredSelectStatus = BrandStatuses.filter(
		(status) =>
			status.value !== BrandStatusEnum.Reported &&
			status.value !== BrandStatusEnum.Locked &&
			status.value !== BrandStatusEnum.NeedsReview
	);
	let selectStatus = filteredSelectStatus;
	let selectStatusValue = selectStatus[0];
	let nameField;
	let urlField;
	let errorPrefix;
	let isSelectedBrand = false;
	let selectedTab;
	let inputRef: any;
	let brandTable: HTMLDivElement;
	let showModal = false;
	let prevMarketplaceInfo: MarketPlaceAcc = {
		marketPlaceIds: [],
		marketPlaceTypes: [],
		marketPlaceRegions: []
	};

	let currentMarketplace: {
		marketPlaceId: string[];
		marketPlaceTypes: string;
		marketPlaceRegions: string;
	} = {
		marketPlaceId: [],
		marketPlaceRegions: '',
		marketPlaceTypes: ''
	};

	const toggleModal = () => {
		showModal = !showModal;
	};

	switch (tab) {
		case '2':
			nameField = 'Profile Name';
			urlField = 'Social Media Profile URL';
			selectedTab = 'SOCIAL_MEDIA';
			errorPrefix = 'Social Media Profile';
			break;
		case '1':
			nameField = 'Name';
			urlField = 'Marketplace Storefront URL';
			selectedTab = 'STORE_FRONT';
			errorPrefix = 'Marketplace Storefront';
			break;
		case '4':
			nameField = 'Name';
			urlField = 'Marketplace Creator URL';
			selectedTab = 'NFT';
			errorPrefix = 'Marketplace Creator';
			break;
		case '3':
			nameField = 'Name';
			urlField = 'Website Creator URL';
			selectedTab = 'WEBSITE';
			errorPrefix = 'Website Creator';
			break;
		default:
			nameField = 'Name';
			urlField = 'URL';
			selectedTab = 'SELLER';
			errorPrefix = 'Marketplace';
	}

	// Subscribe to the page store to get the id parameter
	$: providerId = $page.params.id;
	const convertToObjectType2 = (input: {
		metadata: {
			[x: string]: any;
			Facebook: string[];
			Instagram: string[];
			Description: string[];
			Website: string[];
		};
		name: string;
		marketplace_profile_url: string;
		country: string;
	}) => {
		return {
			storeFront: input?.metadata?.storeFront
				? input?.metadata?.storeFront
				: '' || ([''] as [string, ...string[]]),
			facebook: input?.metadata?.facebook || ([''] as [string, ...string[]]),
			instagram: input?.metadata?.instagram || ([''] as [string, ...string[]]),
			full_name: input?.name,
			url: [input?.marketplace_profile_url] || ([''] as [string, ...string[]]),
			country: input?.country,
			description: input?.metadata?.description,
			website: input?.metadata?.website || ([''] as [string, ...string[]]),
			email: input?.metadata?.email || ('' as string),
			business_name: input?.metadata?.business_name || ('' as string),
			address: input?.metadata?.address || ('' as string),
			city: input?.metadata?.city || ('' as string),
			state: input?.metadata?.state || ('' as string),
			zip: input?.metadata?.zip || ('' as string)
		};
	};

	if (isClone) {
		const cloneSellerData = {
			full_name: data?.providerFromDetail?.name,
			url: [data?.providerFromDetail?.marketplace_profile_url],
			country: data?.providerFromDetail?.country,
			description: data?.providerFromDetail?.metadata?.description
		};
		cloneSellerDataStore.set(cloneSellerData);
		oldBrandIds = data?.providerFromDetail?.brand_providers?.map((val) => {
			return val.brand_id;
		});
		$selectedRowIds = oldBrandIds;
	}

	if (isEdit) {
		const editSellerData = convertToObjectType2(data?.providerFromDetail);
		oldBrandIds = data?.providerFromDetail?.brand_providers?.map((val) => {
			return val.brand_id;
		});
		$statusData = data?.providerFromDetail?.brand_providers?.map((val) => {
			return { brand_id: val.brand_id, status: val.status };
		});
		$statusLocal = data?.providerFromDetail?.brand_providers?.map((val) => {
			return { brand_id: val.brand_id, status: val.status };
		});

		(editSellerData.country =
			editSellerData.country == '' ? ({ label: '', value: '' } as any) : editSellerData.country),
			($selectedRowIds = oldBrandIds);
		editSellerDataStore.set(editSellerData);
		statusData.subscribe((value) => {
			statusDetail = value;
		});
		// store current marketplace
		currentMarketplace = {
			marketPlaceId: [data?.providerFromDetail?.marketplace?.id],
			marketPlaceRegions: data?.providerFromDetail?.marketplace?.marketplace_region,
			marketPlaceTypes: data?.providerFromDetail?.marketplace?.marketplace_type
		};
	}

	tainted.subscribe((val) => {
		isTainted = val;
	});

	form.update(
		($form) => {
			$form = !isEdit ? (!isClone ? initialization : $cloneSellerDataStore) : $editSellerDataStore;
			return $form;
		},
		{ taint: false }
	);
	$: option = ($countryData.data as { label: string; value: string }[])?.map(
		(country: { label: string; value: string }) => {
			return {
				label: country.label,
				value: country.value
			};
		}
	);

	$: sellerStatusListingCounts = $getSellerStatusListingCounts?.data as SellerStatusListingCounts;
	// fetch the prev marketplace info for the selected brands
	$: if ($brands.data) {
		getMarketplaceInfo({ isPrev: true });
	}

	$: dataCount = ($brands?.data as { count: number })?.count || 0;
	$: {
		const newRows =
			($brands.data as { rows: Brand[] })?.rows?.map((item: Brand) => {
				let statusDataRes: { status: string; brand_id: string } | undefined;
				statusDataRes = item?.brand_providers?.find(
					(brandProvider: { provider_id: string; brand_id: String }) =>
						brandProvider.provider_id === providerId && brandProvider.brand_id === item.id
				);
				// Assuming `statusDetail` is an array of objects and `item` is the current item being checked

				const foundStatus = $statusLocal?.find(
					(st: { brand_id: string }) => st.brand_id === item.id
				);
				const index = $selectedRowIds?.find((row) => row === item.id);
				if (!foundStatus && statusDataRes && statusDataRes?.status && index) {
					// Update the statusData store with the new brand_id and status
					statusData.update((currentData) => {
						return [
							...currentData,
							{ brand_id: item.id, status: statusDataRes?.status || BrandStatusEnum.Undetermined }
						];
					});
				}

				statusLocal.update((currentData) => {
					const exist = currentData.find((cd) => cd.brand_id === item.id);

					if (exist) return currentData;

					return [
						...currentData,
						{ brand_id: item.id, status: statusDataRes?.status || BrandStatusEnum.Undetermined }
					];
				});

				statusData.subscribe((value) => {
					statusDetail = value;
				});

				if ($statusLocal.length > 0) {
					statusDataRes = $statusLocal.find((st: { brand_id: string }) => st.brand_id === item.id);
				}
				return {
					id: item.id,
					imgUrl: item.logo_path || '',
					name: item.name,
					licensor: item.licensor.display_name,
					marketplaces: item.brand_marketplaces.map((item) => item.marketplace?.display_name),
					status: statusDataRes?.status || BrandStatusEnum.Undetermined
				};
			}) || [];
		rowDat.update(() => [...newRows]);
	}

	rowDat.subscribe((val) => {
		brandData = val;
		selectedMarketplaceBrands.update(() =>
			$selectedRowIds.filter((item) => {
				if (brandData?.find((bd) => bd.id === item)) return true;
				else return false;
			})
		);
	});

	//create the marketplace info for handling marketplace for seller website
	const getMarketplaceInfo = (arg: { isPrev: boolean; comparePrev?: boolean }) => {
		const brandMarketplaces = ($brands.data as { rows: Brand[] })?.rows?.filter((item) =>
			$selectedRowIds.includes(item.id)
		);
		const uniqueMarketplaces = Array.from(
			new Map(
				brandMarketplaces
					.flatMap((item) => item.brand_marketplaces.map((t) => t.marketplace))
					.map((marketplace) => [marketplace.id, marketplace])
			).values()
		);
		if (isEdit && arg.isPrev) {
			prevMarketplaceInfo = uniqueMarketplaces?.reduce(
				(acc: MarketPlaceAcc, item) => {
					acc.marketPlaceIds = [...acc.marketPlaceIds, item.id];
					acc.marketPlaceTypes = [...acc.marketPlaceTypes, item.marketplace_type];
					acc.marketPlaceRegions = [...acc.marketPlaceRegions, item.marketplace_region];
					return acc;
				},
				{ marketPlaceIds: [], marketPlaceTypes: [], marketPlaceRegions: [] }
			);
		} else {
			if (arg.comparePrev && prevMarketplaceInfo) {
				// Efficient comparison using filtering directly
				const newMarketplaces = uniqueMarketplaces.filter((marketplace) => {
					return (
						!prevMarketplaceInfo.marketPlaceIds.includes(marketplace.id) ||
						!prevMarketplaceInfo.marketPlaceTypes.includes(marketplace.marketplace_type) ||
						!prevMarketplaceInfo.marketPlaceRegions.includes(marketplace.marketplace_region)
					);
				});

				return newMarketplaces;
			} else {
				return uniqueMarketplaces;
			}
		}
		return [];
	};
	const defaultColumns = createSellerBrandColumns({
		onSellerBrandStatus: async (status: string, id: string) => {
			statusData.update(($current) => {
				const brandStatus = { status: status, brand_id: id, prev_status: status };

				const selectIndex = $selectedRowIds.findIndex((item) => item === id);

				if (selectIndex !== -1) {
					const index = $current?.findIndex((item) => item.brand_id === id);

					if (index !== -1) {
						// If the id exists, update the status
						$current[index].prev_status = $current[index].status;
						$current[index].status = status;
					} else {
						// If the id does not exist, add the new object
						$current.push(brandStatus);
					}

					return $current;
				} else return $current;
			});
			statusLocal.update(($current) => {
				const brandStatus = { status: status, brand_id: id, prev_status: status };
				const index = $current?.findIndex((item) => item.brand_id === id);
				if (index !== -1) {
					// If the id exists, update the status
					$current[index].prev_status = $current[index].status;
					$current[index].status = status;
				} else {
					// If the id does not exist, add the new object
					$current.push(brandStatus);
				}
				return $current;
			});
			const index = brandData.findIndex((row) => row.id === id);
			if (index !== -1) {
				let updatedRow = { ...brandData[index], status: status };

				brandData[index] = updatedRow;
				rowDat.set([...brandData]);
			}
		}
	});
	const handleBackNavigation = () => {
		goto(backUrl);
	};

	const addField = (filedName: string, event: { preventDefault: () => void }) => {
		event.preventDefault();
		const updatedMarketplaceURL = [...$form[filedName], ''];

		form.update(
			($form) => {
				$form = { ...$form, [filedName]: updatedMarketplaceURL };
				return $form;
			},
			{ taint: false }
		);
	};

	const removeField = (filedName: string, item: number, event: { preventDefault: () => void }) => {
		event.preventDefault();
		const updateURL = $form[filedName].filter((data: string, index: number) => item !== index);

		form.update(
			($form) => {
				$form = { ...$form, [filedName]: updateURL as [string, ...string[]] };
				return $form;
			},
			{ taint: false }
		);
	};

	const submitForm = (event: { preventDefault: () => void }) => {
		event.preventDefault();
		if (formElement) {
			formElement.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
		}
	};

	const cancelForm = () => {
		reset();
	};

	const deleteProvider = async (id: string) => {
		await $deleteSeller.mutateAsync(id);
		goto(`/uilib/sellers/list?tab=${tab}`);
	};

	const faultyUrl = (
		response: MarketPlaceAcceptableUrls,
		urls: string[],
		errorMsg: string,
		type: string
	) => {
		if (type === 'url') {
			return urls.map((url) =>
				Object.keys(response).some((key) => url.includes(key))
					? { field: 'url', message: '' }
					: { field: 'url', message: errorMsg }
			);
		} else if (type === 'facebook') {
			return urls.map((url) =>
				Object.values(response).some(
					(key) => url.includes(key.marketplace_name) && key.marketplace_name === 'facebook'
				)
					? { field: 'facebook', message: '' }
					: { field: 'facebook', message: 'Please enter valid Facebook url.' }
			);
		} else if (type === 'instagram') {
			return urls.map((url) =>
				Object.values(response).some(
					(key) =>
						(url.includes(key.marketplace_name) && key.marketplace_name === 'instagram') ||
						key.marketplace_name === 'instagram'
				)
					? { field: 'instagram', message: '' }
					: { field: 'instagram', message: 'Please enter valid Instagram url.' }
			);
		}
	};

	const fetchMarketplaceUrlData = async (urls: string[], type: string) => {
		try {
			const errorMsg =
				' You are currently not subscribed to this marketplace. For more information on how to access this, please contact your CounterFind Account Manager.';

			const response: MarketPlaceAcceptableUrls = (await $getMarketPlaceUrl.mutateAsync({
				urls,
				type: selectedTab
			})) as MarketPlaceAcceptableUrls;

			const isValidUrl = faultyUrl(response, urls, errorMsg, type);

			if (!isValidUrl?.every((ele: { field: string; message: string }) => ele.message === '')) {
				let elementId;
				switch (isValidUrl?.[0]?.field) {
					case 'instagram':
						elementId = 'instagram0';
						break;
					case 'facebook':
						elementId = 'facebook0';
						break;
					default:
						elementId = 'url';
						break;
				}
				const inputElement = document.getElementsByName(elementId);
				setTimeout(() => {
					inputElement?.[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}, 100);

				return {
					success: false,
					error: isValidUrl?.map((item) => item.message)
				};
			}

			if (Object.keys(response).length === urls.length) {
				return { success: true, data: response };
			} else {
				return { success: false, error: [' Both Urls are same'] };
			}
		} catch (error: any) {
			return { success: false, error: error.message || 'An error occurred' };
		}
	};
	const setUrlErrorAtIndex = (index: number, errorMessage: any) => {
		errors.update((currentErrors) => {
			// Create a copy of the existing errors array (or ensure it's an array of the correct length)

			let updatedErrors = [...(currentErrors.url || [])];
			// Ensure the array has the required length by filling with empty strings
			if (updatedErrors.length < index + 1) {
				updatedErrors = [...updatedErrors, ...Array(index + 1 - updatedErrors.length).fill('')];
			}

			// Set the error message at the specified index
			updatedErrors[index] = errorMessage;

			const inputElement = document.getElementById('urlInput');
			setTimeout(() => {
				inputElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}, 100);

			// Return the updated errors state
			return {
				...currentErrors,
				url: updatedErrors
			};
		});
	};

	const handleSubmit = async (event: { preventDefault: () => void }) => {
		event.preventDefault();

		if ($selectedRowIds?.length > 0) {
			isSelectedBrand = false;
		} else {
			isSelectedBrand = true;
			brandTable?.scrollIntoView({ behavior: 'smooth' });
		}

		const result = await validate();

		if (!result.valid) {
			const firstElement = Object.keys(result?.errors)?.[0];
			const inputElement = document.getElementsByName(firstElement)?.[0];

			setTimeout(() => {
				inputElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}, 100); // Adjust the timeout if needed
		}
		Object?.keys(result.errors).length > 0;

		let marketplaceInfo: MarketPlaceAcc = {
			marketPlaceIds: [],
			marketPlaceTypes: [],
			marketPlaceRegions: []
		};
		//create website seller logic
		if (result.valid && tab === '3') {
			const getUniqueMarketplace = isEdit
				? getMarketplaceInfo({ isPrev: false, comparePrev: true })
				: getMarketplaceInfo({ isPrev: false });
			marketplaceInfo = getUniqueMarketplace?.reduce(
				(acc: MarketPlaceAcc, item) => {
					acc.marketPlaceIds = [...acc.marketPlaceIds, item.id];
					acc.marketPlaceTypes = [...acc.marketPlaceTypes, item.marketplace_type];
					acc.marketPlaceRegions = [...acc.marketPlaceRegions, item.marketplace_region];
					return acc;
				},
				{ marketPlaceIds: [], marketPlaceTypes: [], marketPlaceRegions: [] }
			);
		} else if (result.valid && !isSelectedBrand) {
			const marketplaceUrlResponse = await fetchMarketplaceUrlData($form.url, 'url');

			if (!marketplaceUrlResponse?.success) {
				formElement?.url?.scrollIntoView({ behavior: 'smooth' });
				errors.update((currentErrors) => ({
					...currentErrors,
					url: [marketplaceUrlResponse.error]
				}));
				if (
					!($errors?.url as Array<string>)?.every((ele: string) => {
						return ele === '';
					})
				) {
					return;
				}
			}
			const urls = $form.url;
			let finalMarketplaces: MarketPlaceAcceptableUrls[] = [];
			if (marketplaceUrlResponse && typeof marketplaceUrlResponse?.data == 'object') {
				finalMarketplaces = urls.map(function (marketPlace: string) {
					let marketplaces = marketplaceUrlResponse.data[
						marketPlace as keyof MarketPlaceAcceptableUrls
					] as unknown as MarketPlaceAcceptableUrls;

					return marketplaces;
				});
			}
			if (finalMarketplaces.length > 0) {
				marketplaceInfo = finalMarketplaces?.reduce(
					(acc: MarketPlaceAcc, item: MarketPlaceAcceptableUrls) => {
						acc.marketPlaceIds = [...acc.marketPlaceIds, item.marketplace_id];
						acc.marketPlaceTypes = [...acc.marketPlaceTypes, item.marketplace_type];
						acc.marketPlaceRegions = [...acc.marketPlaceRegions, item.marketplace_region];
						return acc;
					},
					{ marketPlaceIds: [], marketPlaceTypes: [], marketPlaceRegions: [] }
				);
			}
		}
		tainted.update((val: any) => {
			const urlFieldLength = $form?.url?.length;
			const urlObj: { [key: number]: boolean } = {};

			// Loop to add properties dynamically
			for (let i = 0; i <= urlFieldLength; i++) {
				urlObj[i] = true;
			}

			if (val?.['url']) {
				return { ...(val['url'] = urlObj) };
			} else {
				return { url: urlObj, ...val };
			}
		});

		if (result.valid && !isSelectedBrand) {
			isSelectedBrand = false;
			if (isClone || !providerId) {
				const seller_details = {
					name: $form.full_name,
					description: $form.description,
					brand_id: $selectedRowIds,
					country: $form.country.value,
					url: $form.url,
					type: selectedTab,
					brandStatus: $statusData,
					metadata:
						tab !== '3'
							? { description: $form.description }
							: {
									description: $form.description,
									email: $form.email,
									business_name: $form.business_name,
									address: $form.address,
									city: $form.city,
									state: $form.state,
									zip: $form.zip
								},
					marketplace_id: marketplaceInfo.marketPlaceIds,
					marketplace_type: marketplaceInfo.marketPlaceTypes,
					marketplace_region: marketplaceInfo.marketPlaceRegions
				};
				const res = (await $creteSellersData.mutateAsync({
					...seller_details
				})) as {
					message: any;
					data?: any;
					success: true;
					statusCode: number;
					index: number;
				};
				if (res?.statusCode === 200) {
					goto(backUrl);
				} else {
					if (res.data?.length)
						res.data.map((val: { index: number; url: any }) => {
							setUrlErrorAtIndex(val.index, [' already exist']);
						});
					setUrlErrorAtIndex(res.index, [res.message]);
				}
			} else {
				let facebookInfo = {};
				let instagramInfo = {};

				if ($form?.facebook?.length > 0 && $form.facebook[0] !== '') {
					const facebookUrlResponse = await fetchMarketplaceUrlData($form.facebook, 'facebook');

					if (!facebookUrlResponse.success) {
						errors.update((currentErrors) => ({
							...currentErrors,
							facebook: facebookUrlResponse.error
						}));
						const error = $errors?.facebook as string[];
						if (
							!error.every((ele: string) => {
								return ele === '';
							})
						) {
							return;
						}
					}
					facebookInfo = Object.values(
						facebookUrlResponse?.data as MarketPlaceAcceptableUrls
					).reduce(
						(acc, item) => {
							acc.marketPlaceIds = [...acc.marketPlaceIds, item.marketplace_id];
							acc.marketPlaceTypes = [...acc.marketPlaceTypes, item.marketplace_type];
							acc.marketPlaceRegions = [...acc.marketPlaceRegions, item.marketplace_region];
							return acc;
						},
						{ marketPlaceIds: [], marketPlaceTypes: [], marketPlaceRegions: [] }
					);
				}

				if ($form.instagram.length > 0 && $form.instagram[0] !== '') {
					const instagramUrlResponse = await fetchMarketplaceUrlData($form.instagram, 'instagram');

					if (!instagramUrlResponse.success) {
						errors.update((currentErrors) => ({
							...currentErrors,
							instagram: instagramUrlResponse.error
						}));
						const error = $errors?.instagram as string[];
						if (
							!error.every((ele: string) => {
								return ele === '';
							})
						) {
							return;
						}
					}
					instagramInfo = Object.values(
						instagramUrlResponse.data as MarketPlaceAcceptableUrls
					).reduce(
						(acc, item) => {
							acc.marketPlaceIds = [...acc.marketPlaceIds, item.marketplace_id];
							acc.marketPlaceTypes = [...acc.marketPlaceTypes, item.marketplace_type];
							acc.marketPlaceRegions = [...acc.marketPlaceRegions, item.marketplace_region];
							return acc;
						},
						{ marketPlaceIds: [], marketPlaceTypes: [], marketPlaceRegions: [] }
					);
				}

				const removeItemArray = oldBrandIds?.filter((item) => !$selectedRowIds.includes(item));
				const addNewItemArray = $selectedRowIds.filter((item) => !oldBrandIds.includes(item));

				const urlArrayWithOnlyFirstElement = $form?.url?.slice(0, 1);
				// Create an array with the remaining elements
				const uralArrayWithRemainingElements = $form?.url?.slice(1);

				// const previousFirstElementOfFormUrl = $form.url.shift();
				const isArrayWithEmptyString = (arr: string) => {
					return Array.isArray(arr) && arr.length === 1 && arr[0] === '';
				};
				// Create an array that contains only the first element
				const storeUrlArrayWithOnlyFirstElement = $form?.storeFront?.slice(0, 1);

				// Create an array with the remaining elements
				const storeUralArrayWithRemainingElements = $form?.storeFront?.slice(1);

				const urlStoreFrontData = $form.url.map((url: string, index: string | number) => ({
					URL: url,
					storeFront: storeUralArrayWithRemainingElements[index]
				}));

				// const previousFirstElementOfForm = $form.storeFront.shift();

				// Convert statusData to a Set of brand_ids for quick lookup
				const statusBrandIds = new Set($statusData.map((item) => item.brand_id));

				// Loop through addNewItemArray and add missing items to statusData
				addNewItemArray.forEach((id) => {
					if (!statusBrandIds.has(id)) {
						$statusData.push({ brand_id: id, status: BrandStatusEnum.Undetermined });
					}
				});

				const filteredStatusData = $statusData.filter(
					(item) => !removeItemArray.includes(item.brand_id)
				);

				const marketPlacesIds = marketplaceInfo.marketPlaceIds;
				const marketPlacesTypes = marketplaceInfo.marketPlaceTypes;
				const marketPlacesRegions = marketplaceInfo.marketPlaceRegions;
				const previousFirstMarketPlaceId =
					tab === '3' ? currentMarketplace.marketPlaceId : marketPlacesIds.shift();
				const previousFirstMarketPlaceType =
					tab === '3' ? currentMarketplace.marketPlaceTypes : marketPlacesTypes.shift();
				const previousFirstMarketPlaceRegions =
					tab === '3' ? currentMarketplace.marketPlaceRegions : marketPlacesRegions.shift();
				const res = (await $updateSellerDetail.mutateAsync({
					seller_details: {
						name: $form.full_name,
						description: $form.description,
						brand_id: { selectedRowIds: $selectedRowIds, removeItemArray, addNewItemArray },
						country: { label: $form?.country?.label, value: $form.country?.value },
						url: urlArrayWithOnlyFirstElement,
						marketplace_id: previousFirstMarketPlaceId,
						marketplace_type: previousFirstMarketPlaceType,
						type: selectedTab,
						marketplace_region: previousFirstMarketPlaceRegions,
						new_url: uralArrayWithRemainingElements,
						new_marketplaceId: marketPlacesIds,
						new_marketplaceType: marketPlacesTypes,
						new_marketplaceRegion: marketPlacesRegions,
						brandStatus: filteredStatusData,
						metadata:
							tab !== '3'
								? {
										website: $form.website,
										description: $form.description,
										storeFront: storeUrlArrayWithOnlyFirstElement
									}
								: {
										website: $form.website,
										description: $form.description,
										storeFront: storeUrlArrayWithOnlyFirstElement,
										email: $form.email,
										business_name: $form.business_name,
										address: $form.address,
										city: $form.city,
										state: $form.state,
										zip: $form.zip
									},
						urlStoreFrontData,
						facebookInfo: facebookInfo,
						instagramInfo: instagramInfo,

						facebook: isArrayWithEmptyString($form.facebook) ? [] : $form.facebook,
						instagram: isArrayWithEmptyString($form.instagram) ? [] : $form.instagram
					},
					id: providerId
				})) as { success: true; statusCode: number; data?: any; index?: number; message?: string };

				if (res?.statusCode === 200) {
					goto(backUrl);
					form.update(
						($form) => {
							$form = initialization;
							return $form;
						},
						{ taint: false }
					);
				} else {
					if (res.data?.length) {
						res.data.map((val: { index: number; url: any }) => {
							const index1 = val.index + 1;

							setUrlErrorAtIndex(index1, ['already exist']);
							// $errors.url = ['', 'currentErrors'];
							// errors.update(() => {
							//     return { url: ['', 'currentErrors'] };
							// });
						});
					}
					setUrlErrorAtIndex(Number(res?.index), [res.message]);
				}
			}
		}
	};
	const callGetData = (brandData: {
		detail: {
			page_size: number;
			page_number: number;
		};
	}) => {
		const newPageSize = brandData?.detail?.page_size;
		const newPageNumber = brandData?.detail?.page_number;

		if (newPageSize !== pageSize || newPageNumber !== pageNumber) {
			pageSize = newPageSize;
			pageNumber = newPageNumber;
			$brands.mutate({
				size: pageSize,
				page: pageNumber,
				providerId: !isEdit ? null : providerId,
				isMarketplaceEnable: true
			});
		}
	};

	const findAddedOrRemoved = (
		firstArray: string[],
		secondArray: {
			status: string;
			brand_id: string;
		}[]
	) => {
		// Convert the second array to a set for easy lookup
		const secondSet = new Set(secondArray.map((item) => item.brand_id));

		// Check if any brand_id in the first array is not in the second array (removed case)
		for (const brandId of firstArray) {
			if (!secondSet.has(brandId)) {
				return { is_added: true, brand_id: brandId };
			}
		}

		// Convert the first array to a set for easy lookup
		const firstSet = new Set(firstArray);

		// Check if any brand_id in the second array is not in the first array (added case)
		for (const item of secondArray) {
			if (!firstSet.has(item.brand_id)) {
				return { is_added: false, brand_id: item.brand_id };
			}
		}

		// If no additions or removals are found
		return null;
	};

	const handleSingleChange = (updatedSelectArray: string[]) => {
		const newRemovedOrAddedId = findAddedOrRemoved(updatedSelectArray, statusDetail);
		isSelectedBrand = false;
		if (newRemovedOrAddedId?.is_added && isEdit) {
			const statusDataRes = $rowDat.find((val) => {
				return val.id === newRemovedOrAddedId?.brand_id;
			});
			statusData.update((currentData) => {
				return [
					...currentData,
					{
						brand_id: newRemovedOrAddedId?.brand_id,
						status: statusDataRes?.status || BrandStatusEnum.Undetermined
					}
				];
			});
		} else {
			statusData.update((currentData) => {
				const newStatusData = currentData.filter((val) => {
					return val.brand_id !== newRemovedOrAddedId?.brand_id;
				});
				return newStatusData;
			});
		}
		if (!isEdit) {
			const checkedBrandStatus = $statusLocal.find(
				(item) => item.brand_id === newRemovedOrAddedId?.brand_id
			);
			statusData.update((current) => {
				const filtered = current.filter((item) => item.brand_id !== newRemovedOrAddedId?.brand_id);

				if (newRemovedOrAddedId?.is_added) {
					filtered.push(
						checkedBrandStatus || {
							brand_id: newRemovedOrAddedId?.brand_id,
							status: BrandStatusEnum.Undetermined
						}
					);
				}
				return filtered;
			});
		}
		$selectedRowIds = updatedSelectArray;
	};

	const handleMultipleChange = (updatedSelectArray: string[], difference: number) => {
		if (difference > 0) {
			statusData.update(() => {
				let newStatus: { status: string; brand_id: string }[] = [];

				updatedSelectArray.forEach((item: string) => {
					newStatus.push(
						$statusLocal.find((ele) => ele.brand_id === item) || {
							brand_id: item,
							status: BrandStatusEnum.Undetermined
						}
					);
				});
				return newStatus;
			});
		} else {
			statusData.update(() => []);
		}
		$selectedRowIds = updatedSelectArray;
	};

	const checkBoxSelect = (data: { detail: { selectedRowIds: string[] } }) => {
		const selectedRowDifference: number =
			data?.detail.selectedRowIds.length - $selectedRowIds.length;

		switch (selectedRowDifference) {
			case 1:
			case -1:
				handleSingleChange(data?.detail.selectedRowIds);
				break;
			default:
				handleMultipleChange(data?.detail.selectedRowIds, selectedRowDifference);
				break;
		}
	};

	const onApply = () => {
		rowDat.update((current) => {
			return current.map((brand) => {
				const index = $selectedRowIds.find((row) => row === brand.id);
				if (index !== undefined) {
					statusData.update((statusRes) => {
						const brandStatus = { status: selectStatusValue?.label, brand_id: brand.id };

						// Check if the id already exists in the current array
						const index = statusRes.findIndex((item) => item.brand_id === brand.id);

						if (index !== -1) {
							// If the id exists, update the status
							statusRes[index].status = selectStatusValue?.label;
						} else {
							// If the id does not exist, add the new object
							statusRes.push(brandStatus);
						}

						return statusRes;
					});

					statusLocal.update((statusRes) => {
						const brandStatus = { status: selectStatusValue?.label, brand_id: brand.id };

						// Check if the id already exists in the current array
						const index = statusRes.findIndex((item) => item.brand_id === brand.id);

						if (index !== -1) {
							// If the id exists, update the status
							statusRes[index].status = selectStatusValue?.label;
						} else {
							// If the id does not exist, add the new object
							statusRes.push(brandStatus);
						}

						return statusRes;
					});

					return { ...brand, status: selectStatusValue?.label };
				}

				return brand;
			});
		});
	};

	onMount(() => {
		$countryData.mutate('COUNTRY');
		let marketTypeFilter;
		switch (tab) {
			case '0':
			case '1':
				marketTypeFilter = 'MULTI_SELLER_PRINT_ON_DEMAND_ECOMMERCE';
				break;
			case '2':
				marketTypeFilter = 'SOCIAL_MEDIA';
				break;
			case '3':
				marketTypeFilter = 'SOCIAL_MEDIA_AND_GOOGLE_SHOPPING';
				break;
			case '4':
				marketTypeFilter = 'NFT';
				break;
			default:
				marketTypeFilter = null;
		}
		$brands.mutate({
			size: pageSize,
			page: pageNumber,
			providerId: !isEdit ? (!isClone ? null : providerId) : providerId,
			isMarketplaceEnable: true,
			active: true,
			marketTypeFilter: marketTypeFilter
		});
		$getSellerStatusListingCounts.mutate(String(providerId));
	});

	let brandCrump = [{ title: data?.providerFromDetail?.name, url: '/uilib/sellers/list' }];

	const clickListing = (key: any) => {
		removeAllListingsFilter();
		addListingsFilter([
			{
				key: ListingFilterEnum.Sellers,
				ids: [providerId]
			},
			{
				key: ListingFilterEnum.Status,
				ids: [key]
			}
		]);

		window.location.href = '/uilib/listings';
	};

	// Function to check if an object is an indexable array
	const isArrayOfStringsOrRecord = (obj: any): obj is string[] | Record<number, string[]> => {
		return Array.isArray(obj) || (typeof obj === 'object' && obj !== null);
	};
</script>

<Modal {showModal}>
	<h5 class="text-center">Are you sure you want to Delete Seller?</h5>
	<div slot="footer" class="flex justify-around">
		<button
			type="button"
			class="mb-2 me-2 w-2/5 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300"
			on:click={() => toggleModal()}
		>
			No
		</button>
		<button
			type="button"
			class="mb-2 me-2 w-2/5 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-800 focus:outline-none focus:ring-4 focus:ring-red-300"
			on:click={() => deleteProvider(providerId)}
		>
			Yes
		</button>
	</div>
</Modal>

<div class="p-5">
	<div class="flex items-center justify-between border-b border-solid border-border pb-5">
		{#if !isEdit}
			<div class="">
				<div class="flex items-center gap-2">
					<button on:click={handleBackNavigation}>
						<BackArrow />
					</button>
					<h3 class="font-inter text-2xl font-bold leading-9 text-dark">{formHeader}</h3>
				</div>
			</div>
		{:else}
			<HeadContent isStatus={true} showimage={false} src={''} title={brandCrump} {backUrl}>
				<span class="inline-block">4.4</span>
				<StarIcon />
				<p class="font-inter text-base font-medium leading-6 text-secondary">(1.7k)</p>
			</HeadContent>
		{/if}

		<div class="btn-wrap ml-auto">
			<Button type="button" onclick={(event) => submitForm(event)}>Save Changes</Button>
		</div>
	</div>
	<!-- use:enhance={form_action({},()=>{invalidate(()=>false)})} -->
	<div class="mx-auto mt-10 max-w-screen-2xl">
		{#if isEdit}
			<DetailsCard count={data?.statusCounts} {clickListing} />
		{/if}

		<div class="w-full gap-1">
			<h5 class="leading-24 font-inter text-xl font-bold text-dark">{formSubHeader}</h5>
		</div>

		<div class="mb-10 mt-5 flex w-full gap-5">
			<!-- w-[calc(100%-360px)] -->
			<div
				class="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark relative w-full rounded-sm border bg-white p-5"
			>
				<form
					bind:this={formElement}
					use:enhance
					method="POST"
					enctype="multipart/form-data"
					on:submit={handleSubmit}
				>
					<div class="mb-[30px]">
						<div class="mb-2.5">
							<Label class="font-inter text-base font-semibold leading-6 text-dark"
								>{nameField}</Label
							>
						</div>
						<div class="relative w-full">
							<Input
								class="width-class input w-full focus:outline-none"
								name="full_name"
								bind:value={$form.full_name}
								aria-invalid={$errors?.full_name ? 'true' : undefined}
							/>
							{#if $errors?.full_name}
								<!-- <div class="mt-1.5 text-red-500">{$errors?.full_name}</div> -->
								<div class="mt-1.5 text-red-500">
									{`${tab == '2' ? 'Profile Name' : 'Name'} is required`}
								</div>
							{/if}
							<!-- error={$errors?.full_name} -->
						</div>
					</div>
					<div class="market mb-[30px]">
						<div class="flex flex-col gap-[30px]" id="url">
							{#each $form.url as item, index}
								<div class="relative">
									<div class="mb-2.5">
										<Label class="font-inter text-base font-semibold leading-6 text-dark"
											>{urlField}</Label
										>
									</div>
									<div class="relative w-full py-2" name={`url_1${index}`}>
										<Input
											class="width-class input font-color w-full focus:outline-none"
											bind:value={$form.url[index]}
											bind:this={inputRef}
											autofocus={$errors?.url?.[index] ? true : undefined}
											name={`url`}
											id="urlInput"
											placeholder={'e.g. https://www.wish.com/merchant/561ae5624c92130f473e372d'}
											aria-invalid={$errors?.url?.[index] ? 'true' : undefined}
										/>
										{#if index !== 0}
											<div class="absolute right-5 top-2/4 -translate-y-2/4">
												<DeleteIcon
													width="20px"
													height="20px"
													on:click={(event) => removeField('url', index, event)}
												/>
											</div>
										{/if}
									</div>
									{#if $errors?.url && isArrayOfStringsOrRecord($errors.url) && $errors.url[index]?.[0]}
										<div class="mt-1.5 text-red-500" id={`url_error_${index}`}>
											{`${
												$errors.url?.[index]?.[0]?.trim() ==
												'You are currently not subscribed to this marketplace. For more information on how to access this, please contact your CounterFind Account Manager.'
													? $errors.url?.[index]?.[0]
													: errorPrefix + $errors.url?.[index]?.[0]
											}`}
										</div>
									{/if}
									{#if providerId && !isClone && tab !== '1'}
										<div class="px-2 py-2">
											<Label>Store Front</Label>
										</div>
										<div class="relative flex w-full items-center px-1 py-2">
											<Input
												class="width-class input font-color"
												bind:value={$form.storeFront[index]}
												aria-invalid={$errors?.storeFront?.[index] ? 'true' : undefined}
											/>
										</div>
										{#if $errors?.storeFront && isArrayOfStringsOrRecord($errors.storeFront) && $errors.storeFront[index]?.[0]}
											<div class="mt-1.5 text-red-500">{$errors?.storeFront?.[index]?.[0]}</div>
										{/if}
									{/if}
								</div>
							{/each}
						</div>
						{#if tab !== '3'}
							<div class="mt-2.5 text-blue-500">
								<OutboundLink
									href=""
									on:click={(event) => {
										{
											addField('url', event);
										}
									}}>+ Add URL</OutboundLink
								>
							</div>
						{/if}

						{#if tab === '0' || tab === '1'}
							<div class="mb-[20px] mt-5">
								<div class="mb-2.5">
									<Label class="font-inter text-base font-semibold leading-6 text-dark"
										>{tab === '0' ? 'Seller ID' : 'Storefront ID'}</Label
									>
								</div>
								<div class="relative w-full">
									<Input
										class="width-class input w-full cursor-not-allowed border-gray-200 bg-gray-100 text-gray-600 focus:outline-none"
										value={data?.providerFromDetail?.marketplace_profile_key || ''}
										disabled
										readonly
									/>
								</div>
							</div>
						{/if}
					</div>
					{#if tab === '3'}
						<div class="mb-[30px]">
							<div class="mb-2.5">
								<Label class="font-inter text-base font-semibold leading-6 text-dark">Email</Label>
							</div>
							<div class="relative w-full">
								<Input
									class="width-class input w-full focus:outline-none"
									name="email"
									bind:value={$form.email}
									autofocus={$errors?.email ? true : undefined}
									aria-invalid={$errors?.email ? 'true' : undefined}
								/>
								{#if $errors?.email}
									<div class="mt-1.5 text-red-500">
										{` ${$errors?.email}`}
									</div>
								{/if}
								<!-- error={$errors?.full_name} -->
							</div>
						</div>
						<div class="mb-[30px]">
							<div class="mb-2.5">
								<Label class="font-inter text-base font-semibold leading-6 text-dark"
									>Business name</Label
								>
							</div>
							<div class="relative w-full">
								<Input
									bind:value={$form.business_name}
									class="width-class input w-full focus:outline-none"
									name="business_address_name"
								/>
							</div>
						</div>
						<div class="mb-[30px]">
							<div class="mb-2.5">
								<Label class="font-inter text-base font-semibold leading-6 text-dark">Address</Label
								>
							</div>
							<div class="relative w-full">
								<TextArea
									class="width-class input w-10 p-2 focus:outline-none"
									placeholder="Address"
									bind:value={$form.address}
								/>
							</div>
							<!-- //country -->

							<div class="mb-2.5">
								<Label class="font-inter text-base font-semibold leading-6 text-dark">Country</Label
								>
							</div>
							<div class="relative w-full">
								<SvelteSelect
									placeholder="please select country"
									options={option}
									clearable={false}
									bind:value={$form.country}
									let:prop={item}
									className="appearance-none"
								>
									<span>{item?.label}</span>
								</SvelteSelect>
							</div>

							<div class="mb-2.5 mt-2 flex w-full justify-around gap-5">
								<div class="flex w-full flex-col">
									<div class=" mb-2.5" aria-invalid={$errors?.country ? 'true' : undefined}>
										<Label class="font-inter text-base font-semibold leading-6 text-dark"
											>City</Label
										>
									</div>
									<Input
										class="width-class input w-full focus:outline-none"
										name="business_address_city"
										bind:value={$form.city}
									/>
								</div>

								<div class="flex w-full flex-col">
									<div class=" mb-2.5" aria-invalid={$errors?.country ? 'true' : undefined}>
										<Label class="font-inter text-base font-semibold leading-6 text-dark"
											>State</Label
										>
									</div>
									<Input
										class="width-class input w-full focus:outline-none"
										name="business_address_state"
										bind:value={$form.state}
									/>
								</div>

								<div class="flex w-full flex-col">
									<div class=" mb-2.5" aria-invalid={$errors?.country ? 'true' : undefined}>
										<Label class="font-inter text-base font-semibold leading-6 text-dark">Zip</Label
										>
									</div>
									<Input
										class="width-class input w-full focus:outline-none"
										name="business_address_zip"
										bind:value={$form.zip}
									/>
								</div>
							</div>
						</div>
					{/if}
					{#if tab !== '3'}
						<div class="mb-[30px]">
							<div class=" mb-2.5" aria-invalid={$errors?.country ? 'true' : undefined}>
								<Label class="font-inter text-base font-semibold leading-6 text-dark">Country</Label
								>
							</div>
							<SvelteSelect
								placeholder="please select country"
								options={option}
								clearable={false}
								bind:value={$form.country}
								let:prop={item}
								className="appearance-none"
							>
								<span>{item?.label}</span>
							</SvelteSelect>
						</div>
					{/if}

					<div class="mb-[30px]">
						<div class=" mb-2.5">
							<Label class="font-inter text-base font-semibold leading-6 text-dark"
								>Description</Label
							>
						</div>
						<div class="relative w-full">
							<TextArea
								bind:value={$form.description}
								name="description"
								class="min-h-[112px] focus:outline-none"
								aria-invalid={$errors?.description ? 'true' : undefined}
							/>
							{#if $errors?.description}
								<div class=" text-red-500">{$errors?.description}</div>
							{/if}
						</div>
					</div>
					{#if isEdit}
						{#if !IS_POST_MVP}
							{#each $form.website as item, index}
								<div class=" px-2 py-2">
									<Label>Website</Label>
								</div>
								<div class="relative flex w-full items-center px-1 py-2">
									<Input class="width-class input font-color" bind:value={item} />
									{#if index !== 0}
										<div class="absolute right-5 top-2/4 -translate-y-2/4">
											<DeleteIcon
												width="20px"
												height="20px"
												on:click={(event) => {
													removeField('website', index, event),
														removeField('storeFront', index, event);
												}}
											/>
										</div>
									{/if}
								</div>
								{#if $errors?.website && isArrayOfStringsOrRecord($errors.website) && $errors.website[index]}
									<div class=" mt-1.5 text-red-500">{$errors?.website?.[index]?.[0]}</div>
								{/if}
							{/each}
							<div class="text-blue-500">
								<OutboundLink href="" on:click={(event) => addField('website', event)}
									>+ Add Website</OutboundLink
								>
							</div>

							{#each $form.facebook as item, index}
								<div class=" px-2 py-2">
									<Label>Facebook</Label>
								</div>

								<div class="relative flex w-full items-center px-1 py-2">
									<Input
										class="width-class input font-color"
										bind:value={item}
										name={`facebook${index}`}
									/>
									{#if index !== 0}
										<div class="absolute right-5 top-2/4 -translate-y-2/4">
											<DeleteIcon
												width="20px"
												height="20px"
												on:click={(event) => removeField('facebook', index, event)}
											/>
										</div>
									{/if}
								</div>
								{#if $errors?.facebook && isArrayOfStringsOrRecord($errors.facebook) && $errors.facebook[index]}
									<div class=" mt-1.5 text-red-500">{$errors?.facebook?.[index]}</div>
								{/if}
							{/each}
							<div class="text-blue-500">
								<OutboundLink href="" on:click={(event) => addField('facebook', event)}
									>+ Add Facebook profile</OutboundLink
								>
							</div>
							{#each $form.instagram as item, index}
								<div class=" px-2 py-2">
									<Label>Instagram</Label>
								</div>

								<div class="relative flex w-full items-center px-1 py-2">
									<Input
										class="width-class input font-color"
										bind:value={item}
										name={`instagram${index}`}
									/>
									{#if index !== 0}
										<div class="absolute right-5 top-2/4 -translate-y-2/4">
											<DeleteIcon
												width="20px"
												height="20px"
												on:click={(event) => removeField('instagram', index, event)}
											/>
										</div>
									{/if}
								</div>
								{#if $errors?.instagram && isArrayOfStringsOrRecord($errors.instagram) && $errors.instagram[index]}
									<div class=" mt-1.5 text-red-500">{$errors?.instagram?.[index]}</div>
								{/if}
							{/each}
							<div class="text-blue-500">
								<OutboundLink href="" on:click={(event) => addField('instagram', event)}
									>+ Add Instagram profile</OutboundLink
								>
							</div>
						{/if}
					{/if}
				</form>
			</div>
			<!-- {#if tab === '3'}
				<div class="w-[350px]">
					<ListingCountBySellerStatus
						status={sellerStatusListingCounts}
						title={'Listings'}
						provider_id={providerId}
						isViewDisabled
						navigatePath={'/uilib/listings'}
						isStatusClickable
					/>
				</div>
			{:else}
				<div class="w-[350px]">
					<SellerStatus
						title="Brands"
						subTitle="View"
						isViewDisabled="true"
						status={data?.brandProviderCount}
						brandId=""
					/>
					<div class="relative mt-5 w-full">
						<MarketPlaceCard
							brandId=""
							isViewDisabled="true"
							data={[
								{
									...(data?.providerFromDetail?.marketplace || {}),
									logo: data?.providerFromDetail?.marketplace?.logo_path
								}
							]}
							title="Marketplace"
							subTitle="Manage"
						/>
					</div>
				</div>
			{/if} -->
		</div>

		<!-- Start Brand Table -->
		<div class="w-full">
			<div
				class={`flex items-center gap-5 ${isSelectedBrand ? 'justify-start' : 'justify-between'}`}
			>
				<div>
					<h4 class="text-xl font-semibold text-black">Brands</h4>
				</div>
				{#if $selectedRowIds?.length > 0}
					<div class="relative my-5 flex items-center gap-2.5">
						<span>Select Seller status for ({$selectedMarketplaceBrands.length}) Brands</span>
						<div class="relative flex w-52 items-center">
							<SvelteSelect
								options={selectStatus}
								bind:value={selectStatusValue}
								placeholder="Please Select Status"
								clearable={false}
								className=""
								let:prop={item}
							>
								<!-- svelte-ignore a11y-click-events-have-key-events -->
								{#if selectStatusValue.label === item?.label}
									<Icon iconName={item?.label} iconColor="#fff" />
								{:else}
									<!--  -->
									<Icon iconName={item?.label} iconColor="#6C757D" />
								{/if}

								<div>{item?.label}</div>
							</SvelteSelect>

							<div class="absolute right-2">
								<ArrowDownIcon />
							</div>
						</div>
						<div>
							<Button onclick={onApply}>Apply</Button>
						</div>
					</div>
				{/if}

				{#if isSelectedBrand}
					<div class=" text-red-500">Brand Is Required</div>
				{/if}
			</div>
			<div bind:this={brandTable}>
				<Table
					bind:defaultData={brandData}
					{defaultColumns}
					{dataCount}
					selectable={true}
					selectAll={true}
					selectedRowIds={$selectedRowIds}
					current={pageNumber}
					page_size={pageSize}
					on:toggle={callGetData}
					on:checkbox={checkBoxSelect}
					rowClass="py-2"
				/>
			</div>
		</div>
		<!-- End Brand Table -->

		<!-- Start Footer -->
		<div class="btn mt-10 flex items-center justify-between">
			<div class="flex items-center gap-2.5">
				<div class="btn-wrap">
					<Button type="button" onclick={(event) => submitForm(event)}
						><SaveIcon /> Save Changes</Button
					>
				</div>
				<div class="btn-wrap">
					<Button primary={false} onclick={cancelForm}>Cancel</Button>
				</div>
			</div>
			{#if isEdit}
				<div class="btn-wrap">
					<Button isDelete={true} class="" onclick={toggleModal}><DeleteIcon />Delete Seller</Button
					>
				</div>
			{/if}
		</div>
		<!-- End Footer -->
	</div>
</div>
